import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { toast } from "sonner";
import {
  driversApi,
  type CreateDriverRequest,
  type UpdateDriverRequest,
} from "../services/driversApi";

const QK = {
  drivers: (activeOnly?: boolean) => ["drivers", activeOnly ?? true],
} as const;

// ── Queries ────────────────────────────────────────────────────────────────

export const useDrivers = (activeOnly = true, enabled = true) =>
  useQuery({
    queryKey: QK.drivers(activeOnly),
    queryFn: () => driversApi.getAll(activeOnly),
    enabled,
  });

// ── Mutations ─────────────────────────────────────────────────────────────

export const useCreateDriver = () =>
  useMutation({
    mutationFn: (data: CreateDriverRequest) => driversApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver onboarded successfully.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create driver.");
    },
  });

export const useUpdateDriver = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDriverRequest }) =>
      driversApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver updated.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update driver.");
    },
  });

export const useDeleteDriver = () =>
  useMutation({
    mutationFn: (id: string) => driversApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver removed.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove driver.");
    },
  });

export const useLinkDriverUser = () =>
  useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      driversApi.linkUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver account linked successfully.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to link account.");
    },
  });

export const usePromoteDriverToUser = () =>
  useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      driversApi.promoteToUser(id, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver promoted to system user. They can now log into the mobile app.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to promote driver.");
    },
  });
